import { Spinner, SpinnerSize } from '@fluentui/react';
import { ClassNamesFromIStyles, IH2OTheme, useClassNames, useTheme } from '@h2oai/ui-kit';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { barChartStylesDefault, IBarChartStyles, loadingSpinnerStyles } from './BarChart.styles';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IBarChartProps {
  data?: ChartData<'bar'> | null;
  options: ChartOptions<'bar'>;
  barChartStyles?: (theme: IH2OTheme) => Partial<IBarChartStyles>;
  loading?: boolean;
  error?: boolean;
}

const emptyData: ChartData<'bar'> = {
  labels: [],
  datasets: [
    {
      label: '',
      data: [],
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  ],
};

const BarChart = ({ data, options, barChartStyles, loading = false, error = false }: IBarChartProps) => {
  const classNames = useClassNames<IBarChartStyles, ClassNamesFromIStyles<IBarChartStyles>>(
    'bar-chart',
    barChartStyles || barChartStylesDefault
  );

  const theme = useTheme();

  const hasData =
    data && data.datasets.length > 0 && data.datasets.some((dataset) => dataset.data && dataset.data.length > 0);

  const chartData = hasData ? data : emptyData;

  return (
    <div className={classNames.barChartRoot}>
      <div className={classNames.barChartContainer}>
        <Bar data={chartData} options={options} />

        {!hasData && !loading && !error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayText}>No Data</div>
          </div>
        )}

        {loading && (
          <div className={classNames.overlayContainer}>
            <Spinner
              styles={loadingSpinnerStyles(theme)}
              className={classNames.loadingSpinner}
              size={SpinnerSize.large}
              label="Loading..."
            />
          </div>
        )}

        {error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayErrorText}>An error occurred</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarChart;
